import React, {ReactElement, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import FormGroup from '../../common/form/FormGroup'
import FormRow from '../../common/form/FormRow'
import FormFieldBox from '../../common/form/FormFieldBox'
import {useForm, useWatch} from 'react-hook-form'
import {NightService} from "../../app/types";
import {yupResolver} from "@hookform/resolvers/yup";
import {useFormSubmission} from "../../common/servercall/useFormSubmission";
import ErrorAlert from "../../common/components/ErrorAlert";
import FormContainer from "../../common/form/FormContainer";
import * as yup from "yup";
import TextInput from "../../common/form/TextInput";
import {getAutocompleteSchedules, regexForNumberWithoutEmergencyNr} from "../../common/util/helpers";
import {useSelector} from "react-redux";
import {selectedSiteSelector} from "../../authentication/redux";

type Props = {
    nightService: NightService
    onSubmit: (formData: NightService) => void
    readonly?: boolean
    buildFormButtons: (isSubmitting: boolean) => ReactElement
}

const schema = () =>
    yup
        .object({
            scheduleUuid: yup
                .string()
                .when(['activationMode'], {
                    is: (activationMode: string) => activationMode === 'AUTO_ON',
                    then: schema => yup.string().trim().required('required-m').nullable(),
                    otherwise: schema => yup.string().trim().nullable(),
                }),
            forwardNumber: yup
                .string()
                .when(['activationMode'], {
                    is: (activationMode: string) => activationMode !== 'OFF',
                    then: schema => yup.string().trim().max(22, 'too-long').required('required-m').matches(regexForNumberWithoutEmergencyNr, {
                            message: 'invalid-phone-number',
                            excludeEmptyString: true,
                        }).nullable(),
                    otherwise: schema => yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
                        message: 'invalid-phone-number',
                        excludeEmptyString: true,
                    }).nullable(),
                }),
        })
        .required()

export const NightServiceForm = ({ nightService, onSubmit, buildFormButtons}: Props) => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)

    const { control, handleSubmit, setError } = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(schema()),
        defaultValues: nightService
    })

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<NightService>(onSubmit, setError)

    const holidayOptions = useMemo(() => {
        return getAutocompleteSchedules(t, 'holidays', site, nightService?.scheduleUuid ?
            {scheduleUuid: nightService.scheduleUuid, scheduleName: nightService?.scheduleName || '', forwardingNumber: ''} : null)
    }, [site, t, nightService])

    const activationModeOptions = useMemo(
        () =>
            ['OFF', 'ON', 'AUTO_ON'].map(mode => ({
                value: `${mode}`,
                label: `${t(`night-service.activation-modes.${mode}`)}`,
            })),
        [t]
    )

    const activationMode = useWatch({
        control,
        name: 'activationMode',
    })

    return (
        <>
            <ErrorAlert showAlert={!!serverError} />
            <FormContainer onSubmit={handleSubmit(submit, onError)}>
                <FormGroup label={''} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <TextInput label={t('night-service.activation-mode')} name={'activationMode'} control={control}
                                isRadio={true} options={activationModeOptions}
                            />
                        </FormFieldBox>
                        <FormFieldBox>
                            <TextInput
                                readonly={false}
                                label={t('executives.screening-alerting.forward-to-number')}
                                name={'forwardNumber'}
                                control={control}
                                required={activationMode !== 'OFF'}
                            />
                        </FormFieldBox>
                    </FormRow>
                    {
                        activationMode === 'AUTO_ON' && <FormRow>
                            <FormFieldBox>
                                <TextInput
                                    readonly={false}
                                    label={t('schedules.schedule')}
                                    name={`scheduleUuid`}
                                    control={control}
                                    required={true}
                                    autoCompleteOptions={holidayOptions}
                                />
                            </FormFieldBox>
                        </FormRow>
                    }
                </FormGroup>
                {buildFormButtons(isSubmitting)}
            </FormContainer>
        </>
    )
}
